import React, { useState } from "react"
import { StepType, TutorialConfig, TutorialMessage } from "../types/tutorial-config"
import { newStep } from "./empty-config"

type Props = {
  config: TutorialConfig
  selectedStepId: string | null
  onSelectStep: (stepId: string) => void
  onAddStep: (type: StepType) => void
  onMoveStep: (stepId: string, direction: "up" | "down") => void
  onDeleteStep: (stepId: string) => void
  onSelectGlobalsTab: (tab: GlobalsTab) => void
  selectedGlobalsTab: GlobalsTab | null
}

export type GlobalsTab = "variables" | "messages" | "search-triggers" | "tree-mappings"

const STEP_TYPES: StepType[] = [
  "standard",
  "click-advance",
  "search-wait",
  "tree-select",
  "drag-drop",
  "modal-wait",
  "final",
]

const STEP_TYPE_ICON: Record<StepType, string> = {
  standard: "📝",
  "click-advance": "👆",
  "search-wait": "🔍",
  "tree-select": "🌳",
  "drag-drop": "↔️",
  "modal-wait": "🪟",
  final: "🏁",
}

const StepList: React.FC<Props> = ({
  config,
  selectedStepId,
  onSelectStep,
  onAddStep,
  onMoveStep,
  onDeleteStep,
  onSelectGlobalsTab,
  selectedGlobalsTab,
}) => {
  const [showAdd, setShowAdd] = useState(false)
  const messagesByStep: Record<string, TutorialMessage[]> = {}
  for (const m of config.messages) {
    if (!messagesByStep[m.injectAfterStep]) messagesByStep[m.injectAfterStep] = []
    messagesByStep[m.injectAfterStep].push(m)
  }

  return (
    <div className="flex flex-col h-full text-sm">
      <div className="px-3 py-2 border-b font-semibold bg-gray-50">Steps</div>
      <div className="flex-1 overflow-y-auto">
        {config.steps.length === 0 && (
          <div className="px-3 py-2 text-gray-500 italic">No steps yet.</div>
        )}
        {config.steps.map((step, i) => {
          const isSelected = selectedStepId === step.id && !selectedGlobalsTab
          return (
            <div key={step.id} className="border-b">
              <div
                className={`flex items-center gap-1 px-2 py-1.5 cursor-pointer ${
                  isSelected ? "bg-indigo-100" : "hover:bg-gray-50"
                }`}
                onClick={() => onSelectStep(step.id)}>
                <span className="w-5">{STEP_TYPE_ICON[step.type]}</span>
                <span className="flex-1 truncate" title={step.id}>
                  {step.id}
                </span>
                <button
                  type="button"
                  className="text-xs text-gray-500 hover:text-gray-800"
                  title="Move up"
                  disabled={i === 0}
                  onClick={(e) => {
                    e.stopPropagation()
                    onMoveStep(step.id, "up")
                  }}>
                  ↑
                </button>
                <button
                  type="button"
                  className="text-xs text-gray-500 hover:text-gray-800"
                  title="Move down"
                  disabled={i === config.steps.length - 1}
                  onClick={(e) => {
                    e.stopPropagation()
                    onMoveStep(step.id, "down")
                  }}>
                  ↓
                </button>
                <button
                  type="button"
                  className="text-xs text-red-600 hover:text-red-800"
                  title="Delete"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (window.confirm(`Delete step "${step.id}"?`)) {
                      onDeleteStep(step.id)
                    }
                  }}>
                  ✕
                </button>
              </div>
              {messagesByStep[step.id]?.map((m) => (
                <div
                  key={m.id}
                  className="pl-8 pr-2 py-0.5 text-xs text-gray-600 italic">
                  ↳ msg &quot;{m.id}&quot; ({m.type})
                </div>
              ))}
            </div>
          )
        })}
      </div>
      <div className="border-t p-2">
        {!showAdd ? (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="w-full px-2 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">
            + Add step
          </button>
        ) : (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">Pick step type:</span>
            {STEP_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  onAddStep(t)
                  setShowAdd(false)
                }}
                className="text-left text-sm px-2 py-1 hover:bg-gray-100 rounded">
                {STEP_TYPE_ICON[t]} {t}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="text-xs text-gray-500 mt-1">
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="border-t bg-gray-50">
        <div className="px-3 py-1.5 font-semibold text-xs uppercase text-gray-500">
          Globals
        </div>
        {([
          ["variables", "Variables"],
          ["messages", "Messages"],
          ["search-triggers", "Search triggers"],
          ["tree-mappings", "Tree mappings"],
        ] as Array<[GlobalsTab, string]>).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelectGlobalsTab(key)}
            className={`w-full text-left px-3 py-1 text-sm ${
              selectedGlobalsTab === key
                ? "bg-indigo-100 font-medium"
                : "hover:bg-gray-100"
            }`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default StepList
export { newStep }
